import type { CheckTaskViewData, MoveTaskViewData, TaskViewData } from '@nova/core';
import type * as Event from './events';

/** Builds the analytics event payload for "Created: New Move Task" from task data. */
export function buildYardTaskCreatedNewMoveAnalyticsEventContext(
  task: MoveTaskViewData
): Event.YardTaskCreatedNewMovePayload {
  return {
    yard_task_id: task.id ?? '',
    yard_task_assignee: task.assignedTo?.id ?? ''
  };
}

/** Builds the analytics event payload for "Opened: Yard Task" from task data. */
export function buildYardTaskOpenedAnalyticsEventContext(
  task: TaskViewData
): Event.YardTaskOpenedPayload {
  const base: Event.YardTaskOpenedPayload = {
    yard_task_id: task.id ?? '',
    yard_task_type: task.type ?? '',
    yard_task_assignee: task.assignedTo?.id ?? '',
    yard_task_duedate: task.dueDateTime ?? '',
    yard_task_description: task.description ?? '',
    yard_task_tags: task.tags ?? [],
    yard_task_status: task.status ?? ''
  };

  if (task.type === 'move') {
    const t = task as MoveTaskViewData;
    return {
      ...base,
      asset_container_id: t.assetContainer?.id ?? '',
      origin_spot_id: t.originSpot?.id ?? '',
      destination_spot_id: t.destinationSpot?.id ?? ''
    };
  }

  if (task.type === 'check') {
    const t = task as CheckTaskViewData;
    return {
      ...base,
      check_entity_type: t.relatedEntity ?? '',
      check_entity_id: t.relatedId ?? '',
      yard_task_category: t.category ?? '',
      yard_task_checklist: t.checklist ?? []
    };
  }

  return base;
}

/** Builds the analytics event payload for "Started: Yard Task" from task data. */
export function buildYardTaskStartedAnalyticsEventContext(
  task: TaskViewData
): Event.YardTaskStartedPayload {
  const base: Event.YardTaskStartedPayload = {
    yard_task_id: task.id ?? '',
    yard_task_type: task.type ?? '',
    previous_yard_task_assignee: task.assignedTo?.id ?? '',
    yard_task_duedate: task.dueDateTime ?? '',
    yard_task_description: task.description ?? '',
    yard_task_tags: task.tags ?? []
  };

  if (task.type === 'move') {
    const t = task as MoveTaskViewData;
    return {
      ...base,
      asset_container_id: t.assetContainer?.id ?? '',
      origin_spot_id: t.originSpot?.id ?? '',
      destination_spot_id: t.destinationSpot?.id ?? ''
    };
  }

  if (task.type === 'check') {
    const t = task as CheckTaskViewData;
    return {
      ...base,
      check_entity_type: t.relatedEntity ?? '',
      check_entity_id: t.relatedId ?? '',
      yard_task_category: t.category ?? '',
      yard_task_checklist: t.checklist ?? []
    };
  }

  return base;
}

/** Builds the analytics event payload for "Updated: Origin Spot" from task data. */
export function buildYardTaskUpdatedOriginSpotAnalyticsEventContext(
  task: MoveTaskViewData
): Event.YardTaskUpdatedOriginSpotPayload {
  return {
    yard_task_id: task.id ?? '',
    yard_task_status: task.status ?? '',
    yard_task_assignee: task.assignedTo?.id ?? '',
    yard_task_duedate: task.dueDateTime ?? '',
    yard_task_description: task.description ?? '',
    yard_task_tags: task.tags ?? [],
    previous_asset_container_id: task.assetContainer?.id ?? '',
    previous_origin_spot_id: task.originSpot?.id ?? ''
  };
}

/** Builds the analytics event payload for "Updated: Destination Spot" from task data. */
export function buildYardTaskUpdatedDestinationSpotAnalyticsEventContext(
  task: MoveTaskViewData
): Event.YardTaskUpdatedDestinationSpotPayload {
  return {
    yard_task_id: task.id ?? '',
    yard_task_status: task.status ?? '',
    yard_task_assignee: task.assignedTo?.id ?? '',
    yard_task_duedate: task.dueDateTime ?? '',
    yard_task_description: task.description ?? '',
    yard_task_tags: task.tags ?? [],
    previous_destination_spot_id: task.destinationSpot?.id ?? ''
  };
}

/** Builds the analytics event payload for "Canceled: Yard Task" from task data. */
export function buildYardTaskCanceledAnalyticsEventContext(
  task: TaskViewData
): Event.YardTaskCanceledPayload {
  return {
    yard_task_id: task.id ?? '',
    yard_task_type: task.type ?? '',
    yard_task_assignee: task.assignedTo?.id ?? '',
    yard_task_duedate: task.dueDateTime ?? '',
    yard_task_description: task.description ?? '',
    yard_task_tags: task.tags ?? [],
    // not implemented yet on the UI
    cancellation_reason:
      (task as TaskViewData & { cancellationReason?: string }).cancellationReason ?? ''
  };
}

/** Builds the analytics event payload for "Completed: Yard Task" from task data. */
export function buildYardTaskCompletedAnalyticsEventContext(
  task: TaskViewData
): Event.YardTaskCompletedPayload {
  return {
    yard_task_id: task.id ?? '',
    yard_task_type: task.type ?? '',
    yard_task_assignee: task.assignedTo?.id ?? '',
    yard_task_duedate: task.dueDateTime ?? '',
    yard_task_description: task.description ?? '',
    yard_task_tags: task.tags ?? []
  };
}

/** Builds the analytics event payload for "Flagged: Yard Task Issue" from task data. */
export function buildYardTaskFlaggedIssueAnalyticsEventContext(
  task: Partial<TaskViewData>
): Event.YardTaskFlaggedIssuePayload {
  return {
    yard_task_id: task.id ?? '',
    yard_task_type: task.type ?? '',
    yard_task_tags: task.tags ?? []
  };
}

/** Builds the analytics event payload for "Unflagged: Yard Task Issue" from task data. */
export function buildYardTaskUnflaggedIssueAnalyticsEventContext(
  task: Partial<TaskViewData>
): Event.YardTaskUnflaggedIssuePayload {
  return {
    yard_task_id: task.id ?? '',
    yard_task_type: task.type ?? '',
    yard_task_tags: task.tags ?? []
  };
}

/** Builds the analytics event payload for "Added: Yard Task Comment" from task data. */
export function buildYardTaskAddedCommentAnalyticsEventContext(
  task: TaskViewData
): Event.YardTaskAddedCommentPayload {
  return {
    yard_task_id: task.id ?? '',
    yard_task_type: task.type ?? '',
    yard_task_assignee: task.assignedTo?.id ?? '',
    yard_task_duedate: task.dueDateTime ?? '',
    yard_task_description: task.description ?? '',
    yard_task_tags: task.tags ?? []
  };
}

/** Builds the analytics event payload for "Filtered: Yard Task Board". */
export function buildYardTaskFilteredAnalyticsEventContext(
  params: Event.TaskBoardFilteredPayload
): Event.TaskBoardFilteredPayload {
  return {
    filter_type: params.filter_type ?? '',
    is_filter_active: params.is_filter_active ?? false
  };
}

/** Builds the analytics event payload for "Switched: Task Board Lane" from task data. */
export function buildYardTaskSwitchedLaneAnalyticsEventContext(
  switchedToLane: string
): Event.TaskBoardSwitchedLanePayload {
  return {
    switched_to_lane: switchedToLane ?? ''
  };
}
